import React from 'react'
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { formatMonto } from '../../common/dashboard-utils'

/**
 * Donut de casos por compañía tercero — **V1.1 (RG-541, 04-09-2026)**.
 * Reemplaza al donut por estado de caso: ahora la torta reparte COMPAÑÍAS dentro de un estado,
 * en vez de estados dentro del total.
 *
 * 🔒 `colorMap` lo inyecta `donuts-pendientes-cia`: los dos donuts que se muestran juntos tienen
 *    que dar el MISMO color a la misma compañía, o la comparación lado a lado engaña.
 */

const COLORES_DEFECTO = [
    '#4F517A', '#007bff', '#28a745', '#dc3545', '#ffc107',
    '#fd7e14', '#6f42c1', '#17a2b8', '#e83e8c', '#20c997',
    '#6610f2', '#343a40'
]

const TooltipCia = ({ active, payload }) => {
    if (!active || !payload?.length) return null
    const dato = payload[0].payload
    return (
        <div style={{ background: '#fff', border: '1px solid #dee2e6', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{dato.compania}</div>
            <div>Cantidad de casos: <strong>{dato.cantidad}</strong></div>
            <div style={{ color: '#6c757d', marginTop: 2 }}>Monto total: {formatMonto(dato.montoTotal || 0)}</div>
        </div>
    )
}

// Las porciones bajo 4% no llevan etiqueta: no alcanza el ancho para el texto.
const renderEtiqueta = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.04) return null
    const RADIAN = Math.PI / 180
    const radio = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radio * Math.cos(-midAngle * RADIAN)
    const y = cy + radio * Math.sin(-midAngle * RADIAN)
    return (
        <text x={x} y={y} fill='white' textAnchor='middle' dominantBaseline='central' fontSize={11} fontWeight='bold'>
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    )
}

// Con `onExportar(entry)`, cada porción descarga los casos de esa compañía (RG-602).
const PendientesPorCiaDonut = ({ data = [], colorMap, showLegend = true, height = 280, onExportar }) => {
    if (!data.length) return <div className='text-center text-muted py-5'>Sin datos</div>

    const colorDe = (entry, index) => {
        if (colorMap) {
            return colorMap[entry.companiaId] || colorMap[entry.compania] || COLORES_DEFECTO[index % COLORES_DEFECTO.length]
        }
        return COLORES_DEFECTO[index % COLORES_DEFECTO.length]
    }

    return (
        <ResponsiveContainer width='100%' height={height}>
            <PieChart>
                <Pie
                    data={data}
                    cx='50%'
                    cy='50%'
                    innerRadius={55}
                    outerRadius={95}
                    dataKey='cantidad'
                    nameKey='compania'
                    labelLine={false}
                    label={renderEtiqueta}
                    cursor={onExportar ? 'pointer' : undefined}
                    onClick={(porcion) => onExportar && onExportar(porcion?.payload ?? porcion)}
                >
                    {data.map((entry, index) => (
                        <Cell key={entry.companiaId || entry.compania} fill={colorDe(entry, index)} />
                    ))}
                </Pie>
                <Tooltip content={<TooltipCia />} />
                {showLegend && (
                    <Legend formatter={(value) => <span style={{ fontSize: 12 }}>{value}</span>} iconSize={10} />
                )}
            </PieChart>
        </ResponsiveContainer>
    )
}

export default PendientesPorCiaDonut
